/**
 * Time & Servings Section for Recipe Import Preview
 *
 * Displays cooking time, preparation time, servings, and calories fields
 * for editing extracted recipe data.
 */

import type { ExtractedRecipe } from '@/types/recipeImport';

interface TimeServingsSectionProps {
  recipe: ExtractedRecipe;
  onChange: (recipe: ExtractedRecipe) => void;
}

export function TimeServingsSection({ recipe, onChange }: TimeServingsSectionProps) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Prep time */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">
          Preparation Time <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={recipe.preparation_time_minutes ?? ''}
            onChange={(e) =>
              onChange({
                ...recipe,
                preparation_time_minutes: e.target.value ? Number(e.target.value) : null,
              })
            }
            placeholder="0"
            min="0"
            className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          />
          <span className="text-sm text-gray-600">minutes</span>
        </div>
      </div>

      {/* Cooking time */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">
          Cooking Time <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={recipe.cooking_time_minutes ?? ''}
            onChange={(e) =>
              onChange({
                ...recipe,
                cooking_time_minutes: e.target.value ? Number(e.target.value) : null,
              })
            }
            placeholder="30"
            min="0"
            className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          />
          <span className="text-sm text-gray-600">minutes</span>
        </div>
      </div>

      {/* Servings */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">
          Servings <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={recipe.servings ?? ''}
            onChange={(e) =>
              onChange({
                ...recipe,
                servings: e.target.value ? Number(e.target.value) : null,
              })
            }
            placeholder="4"
            min="1"
            className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          />
          <span className="text-sm text-gray-600">portions</span>
        </div>
      </div>

      {/* Calories */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">
          Calories per Serving <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={recipe.calories_per_portion ?? ''}
            onChange={(e) =>
              onChange({
                ...recipe,
                calories_per_portion: e.target.value ? Number(e.target.value) : null,
              })
            }
            placeholder="450"
            min="0"
            className="w-20 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          />
          <span className="text-sm text-gray-600">kcal</span>
        </div>
      </div>
    </div>
  );
}
